import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import cx from 'classnames';
import { ArrowDownIcon } from '@move-ui/icons-asset-catalog';
import { useTranslation } from '../../utils/translations';
import { useTheme } from '../../utils/theme';
import { useCurrentUser } from '../../utils/currentUser';
import Icon, { MenuIcon } from '../Icon';
import MenuHeaderUser from './MenuHeaderUser';
import styles from './MenuHeader.css';

type Props = {
  className?: string;
  dataTestId?: string;
  icon?: string | MenuIcon | ReactNode;
  label: string;
  name: string;
  onClick?: HTMLAttributes<HTMLButtonElement>['onClick'];
};

const MenuHeader = forwardRef<HTMLButtonElement, Props>(
  ({ className, icon, label, onClick, dataTestId, ...rest }, ref) => {
    const {
      isLoggedIn,
      hasUnreadMessages,
      hasUnreadNotifications,
      username
    } = useCurrentUser();
    const { isBranded, isTakeover } = useTheme();
    const { translate } = useTranslation();

    const isBadgeVisible =
      icon === 'User' && (hasUnreadMessages || hasUnreadNotifications);

    const labels = label.split('|');
    const labelText =
      labels.length === 2 && labels[0] === 'username' && isLoggedIn
        ? username || translate(labels[1])
        : translate(label);

    return (
      <button
        className={cx(styles.component, className)}
        data-testid={dataTestId}
        ref={ref}
        role="menuitem"
        type="button"
        onClick={onClick}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      >
        <div className={styles.iconWrapper}>
          {icon && (
            <Icon
              badge={isBadgeVisible}
              className={cx({
                [styles.icon]: icon !== 'User',
                [styles.smallUserIcon]: icon === 'User'
              })}
              icon={icon}
            />
          )}
          <ArrowDownIcon
            className={cx(styles.arrow, {
              [styles.branded]: isBranded,
              [styles.takeover]: isTakeover
            })}
          />
        </div>
        <span
          className={cx(styles.text, {
            [styles.branded]: isBranded,
            [styles.takeover]: isTakeover
          })}
        >
          {labelText}
        </span>
        {icon === 'User' && (
          <MenuHeaderUser
            badge={isBadgeVisible}
            className={styles.userIcon}
            isBranded={isBranded}
          />
        )}
      </button>
    );
  }
);

export default MenuHeader;
